import cn from 'classnames';
import * as React from 'react';

import useInputValidation from '../use-input-validation';

import Option from '../select-option/select-option';
import Text from '../text';
import SvgArrowDown from '../icons/18/arrow-down';

import { labelPositions, Select as Props, variants } from './select.types';

// NOTE: `onChangeOption` works like `onChange` but returns the props for that option instead of an event object.
const RefSelect = React.forwardRef<
  HTMLSelectElement,
  React.PropsWithChildren<Props>
>(
  (
    {
      children,
      className,
      id,
      isLabelVisible = true,
      label,
      labelPosition = labelPositions.stacked,
      name,
      onChange = () => {},
      onChangeOption = () => {},
      options = [],
      required,
      variant = variants.large,
      wide,
      ...restProps
    },
    ref,
  ) => {
    const [validationOnChange, onInvalid, error] = useInputValidation();

    const onInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e);
      onChangeOption(options.find(({ value }) => value === e.target.value));
      validationOnChange(e);
    };

    const asterix = required ? (
      <span className="select__asterisk">*</span>
    ) : null;

    return (
      <div className={cn('select', className, { 'select--error': error })}>
        <div
          className={cn(
            `select__content select__content--position-${labelPosition}`,
            { 'select__content--wide': wide },
          )}
        >
          {isLabelVisible ? (
            <label className="select__label" htmlFor={id}>
              {label || children ? (
                <div className="select__label-text">
                  <Text
                    size={
                      variant === variants.small
                        ? Text.sizes.small
                        : Text.sizes.basic
                    }
                    theme={Text.themes.emphasis}
                  >
                    {label}
                  </Text>
                  {asterix}
                  {children}
                </div>
              ) : required ? (
                asterix
              ) : null}
            </label>
          ) : null}
          <div
            className={cn('select__native-wrapper', {
              'select__native-wrapper--wide': wide,
            })}
          >
            <select
              aria-label={isLabelVisible ? undefined : label}
              className={cn(
                `select__select select__select--variant-${variant}`,
                {
                  'select__select--wide': wide,
                },
              )}
              id={id}
              name={name}
              onChange={onInputChange}
              onInvalid={e => {
                // NOTE: To not break compatibility
                if (restProps.onInvalid) restProps.onInvalid(e);
                onInvalid(e);
              }}
              ref={ref}
              required={required}
              {...restProps}
            >
              {options.map(option => (
                <Option key={option.value} {...option} />
              ))}
            </select>
            <span className="select__chevron">
              <SvgArrowDown />
            </span>
          </div>

          {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
          {error ? (
            <div aria-hidden="true" className="select__error">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

// NOTE: If this isn't set, stack traces say "forwardRef" instead of "Select"
RefSelect.displayName = 'Select';

// NOTE: Since `React.forwardRef` props aren't generic, the component is cast so that generics work as intended. See https://stackoverflow.com/a/58473012
const SelectComponent = RefSelect as <OptionExtra>(
  props: Props<OptionExtra> & {
    ref?: React.Ref<HTMLSelectElement>;
  },
) => React.ReactElement;

const Select: typeof SelectComponent & {
  labelPositions: typeof labelPositions;
  variants: typeof variants;
} = Object.assign(SelectComponent, { labelPositions, variants });

export default Select;
